import { Directive, ElementRef, inject, Input, OnDestroy } from '@angular/core';
import { KeyValue } from '@angular/common';
import { KeyboardListenerService } from '../../services/keyboard-listener.service';
import { Subscription } from 'rxjs';

@Directive({
	selector: '[evasysKeyboardSearch]',
})
export class KeyboardSearchDirective implements OnDestroy {
	//region Inputs & Outputs
	@Input()
	allowSelect = true;
	@Input()
	allowScroll = true;

	@Input('evasysKeyboardSearch')
	set isActive(isActive: boolean) {
		if (isActive) {
			this.typedSubscriptions.push(
				this.keyboardListenerService.currentTyped.subscribe(this.onCurrentTypedChange),
				this.keyboardListenerService.onArrowTyped.subscribe(this.onArrowTyped)
			);
		} else {
			this.typedSubscriptions.forEach((subscription) => subscription?.unsubscribe());
		}
	}

	//endregion
	//region Variables
	private readonly elements: KeyValue<ElementRef, string>[] = [];
	private selectedElement?: ElementRef;
	private typedSubscriptions: Subscription[] = [];
	private searchValue = '';
	private searchPointer = -1;
	private lastTypeTimestamp?: number;
	private lastSearch?: string;

	//endregion

	//region Injections
	private readonly keyboardListenerService = inject(KeyboardListenerService);
	private readonly hostElement = inject(ElementRef);
	//endregion

	//region Events
	ngOnDestroy() {
		this.typedSubscriptions.forEach((subscription) => subscription?.unsubscribe());
	}

	private handleSearchValue(debounceSeconds: number, currentSearchValue?: string) {
		this.searchValue += currentSearchValue;
		//clear searchValue if debounceSeconds are expired
		const date = new Date();
		if (this.lastTypeTimestamp && this.lastTypeTimestamp < date.getTime()) {
			this.searchValue = currentSearchValue;
		}
		date.setSeconds(date.getSeconds() + debounceSeconds);
		this.lastTypeTimestamp = date.getTime();
	}
	/**
	For navigating through findings with one letter
	Example: [Anders, Andreas] - First keydown "a", Anders is selected. Second keydown "a", Andreas is selected.
	 **/
	private handleSearchPointer(foundElements: KeyValue<ElementRef, string>[], currentSearchValue?: string) {
		if (currentSearchValue === this.lastSearch) {
			if (this.searchPointer >= foundElements.length - 1) {
				this.searchPointer = 0;
			} else {
				this.searchPointer += 1;
			}
		} else {
			this.searchPointer = 0;
			this.lastSearch = currentSearchValue;
		}
	}

	private handleScroll(foundElements: KeyValue<ElementRef, string>[]) {
		if (this.allowScroll) {
			if (foundElements[this.searchPointer]?.key?.nativeElement) {
				foundElements[this.searchPointer].key.nativeElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'center',
				});
			} else {
				console.error('ElementRef for key actions (scroll) not found!', foundElements, this.searchPointer);
			}
		}
	}

	private handleSelect(foundElements: KeyValue<ElementRef, string>[]) {
		if (this.allowSelect) {
			if (foundElements[this.searchPointer]?.key) {
				foundElements[this.searchPointer].key.nativeElement.dispatchEvent(new Event('mousedown'));
				foundElements[this.searchPointer].key.nativeElement.dispatchEvent(new Event('mouseup'));
				foundElements[this.searchPointer].key.nativeElement.dispatchEvent(new Event('click'));
				foundElements[this.searchPointer].key.nativeElement.focus();
				this.selectedElement = foundElements[this.searchPointer].key;
			} else {
				console.error('ElementRef for key actions (select) not found!', foundElements, this.searchPointer);
			}
		}
	}

	onArrowTyped = (event: KeyboardEvent) => {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			let selectedElementIndex = this.elements.findIndex((element) => element.key === this.selectedElement);
			if (event.key === 'ArrowDown') {
				selectedElementIndex += 1;
			} else {
				selectedElementIndex -= 1;
			}
			if (selectedElementIndex >= 0 && selectedElementIndex < this.elements.length) {
				this.searchPointer = 0;
				this.handleSelect([this.elements[selectedElementIndex]]);
				this.handleScroll([this.elements[selectedElementIndex]]);
				event.preventDefault();
			}
		}
	};

	onCurrentTypedChange = (currentSearchValue?: string) => {
		this.handleSearchValue(1.5, currentSearchValue);
		let searchValue = this.searchValue;
		//remove duplicates
		const searchTab = [...new Set(this.searchValue?.toLowerCase().split(''))];
		if (searchTab && searchTab.length === 1) {
			searchValue = searchTab[0];
		}
		const foundElements = this.elements?.filter((keyValue) => keyValue.value.toLowerCase().startsWith(searchValue));
		this.handleSearchPointer(foundElements, currentSearchValue);
		if (
			foundElements &&
			foundElements.length > 0 &&
			this.selectedElement !== foundElements[this.searchPointer].key &&
			this.hostElement.nativeElement.contains(foundElements[this.searchPointer]?.key.nativeElement)
		) {
			this.handleScroll(foundElements);
			this.handleSelect(foundElements);
		}
	};
	//endregion
	//region Methods
	registerElement(elementRef: ElementRef, value: string) {
		this.elements.push({ key: elementRef, value });
	}
	//endregion
}
